import {
  HermesError,
  HermesTokenResult,
  RefreshTokenExchangeResult,
} from '@kaligo/hermes';
import { Action } from '@ngrx/store';
import {
  GuardHouseLogoutCodes,
  HttpError,
  TransactionAppState,
  User,
  UserPreference,
} from '@rd/types';

export enum AuthActionTypes {
  INIT_LOGIN_REDIRECT = '[Auth] Init Login Redirect',
  HANDLE_LOGIN_REDIRECT = '[Auth] Handle Login Redirect',
  SET_LOGIN_REF = '[Auth] Set Login Ref',
  START_IFRAME_TOKEN_REFRESH = '[Auth] Start Iframe Token Refresh',
  START_RTR_TOKEN_REFRESH = '[Auth] Start RTR Token Refresh',
  START_SESSION_CHECK = '[Auth] Start Session Check',
  START_IDLE_CHECK = '[Auth] Start Idle Check',

  REDIRECT_TO_LOGIN = '[Auth] Redirect To Login',

  LOAD_IFRAME_TOKEN_SUCCESS = '[Auth] Load Iframe Token Success',
  LOAD_RTR_TOKEN_SUCCESS = '[Auth] Load RTR Token Success',
  LOGIN_USER = '[Auth] Login User',
  LOAD_TOKEN_USER_PREFERENCES = '[Auth] Load Token User Preferences',

  LOGOUT_USER = '[Auth] Logout User',
  LOGOUT_USER_COMPLETE = '[Auth] Logout User Complete',

  // General actions dispatched from HermesService, to be caught and handled by AuthEffects
  LOAD_TOKEN_FAIL = '[Auth] Load Token Fail',
  SESSION_CHECK_FAIL = '[Auth] Session Check Fail',

  // When user is not logged in anymore
  SESSION_EXPIRED_FAIL = '[Auth] Session Expired Fail',

  // Internal failures returned from Hermes response
  LOAD_TOKEN_REDIRECT_REQUIRED_FAIL = '[Auth] Load Token Redirect Required Fail',
  LOAD_TOKEN_OTHER_FAIL = '[Auth] Load Token Other Fail',
  SESSION_CHECK_OTHER_FAIL = '[Auth] Session Check Other Fail',

  // Internal failures returned from NN response
  INVALID_SCOPE_FAIL = '[Auth] Invalid Scope Fail',
  INTERNAL_FAIL = '[Auth] Internal Fail',
  MAXIMUM_RETRIES_FAIL = '[Auth] Maximum Retries Fail',
  NO_PERMISSION_FAIL = '[Auth] No Permission Fail',
  TOKEN_EXPIRED_FAIL = '[Auth] Token Expired Fail',

  UPDATE_LOCALE_COOKIE = '[Auth] Update Locale Cookie',
}

export class InitLoginRedirect implements Action {
  readonly type = AuthActionTypes.INIT_LOGIN_REDIRECT;
}

export class HandleLoginRedirect implements Action {
  readonly type = AuthActionTypes.HANDLE_LOGIN_REDIRECT;
}

export class SetLoginRef implements Action {
  readonly type = AuthActionTypes.SET_LOGIN_REF;

  constructor(public payload: string) {}
}

export class StartIframeTokenRefresh implements Action {
  readonly type = AuthActionTypes.START_IFRAME_TOKEN_REFRESH;
}

export class StartRTRTokenRefresh implements Action {
  readonly type = AuthActionTypes.START_RTR_TOKEN_REFRESH;

  constructor(public payload: TransactionAppState | null) {}
}

export class RedirectToLogin implements Action {
  readonly type = AuthActionTypes.REDIRECT_TO_LOGIN;
}

export class StartSessionCheck implements Action {
  readonly type = AuthActionTypes.START_SESSION_CHECK;
}

export class StartIdleCheck implements Action {
  readonly type = AuthActionTypes.START_IDLE_CHECK;
}

export class LoadIframeTokenSuccess implements Action {
  readonly type = AuthActionTypes.LOAD_IFRAME_TOKEN_SUCCESS;

  constructor(public payload: { tokenResult: HermesTokenResult }) {}
}

export class LoadRTRTokenSuccess implements Action {
  readonly type = AuthActionTypes.LOAD_RTR_TOKEN_SUCCESS;

  constructor(
    public payload: {
      tokenResult: RefreshTokenExchangeResult;
      transactionAppState?: TransactionAppState;
    },
  ) {}
}

export class LoginUser implements Action {
  readonly type = AuthActionTypes.LOGIN_USER;

  constructor(public payload: User) {}
}

export class LoadTokenUserPreferences implements Action {
  readonly type = AuthActionTypes.LOAD_TOKEN_USER_PREFERENCES;

  constructor(public payload: UserPreference) {}
}

export class LogoutUser implements Action {
  readonly type = AuthActionTypes.LOGOUT_USER;

  constructor(
    public payload: GuardHouseLogoutCodes = GuardHouseLogoutCodes.UnIntentionalLogout,
  ) {}
}

export class LogoutUserComplete implements Action {
  readonly type = AuthActionTypes.LOGOUT_USER_COMPLETE;
}

export class LoadTokenFail implements Action {
  readonly type = AuthActionTypes.LOAD_TOKEN_FAIL;

  constructor(public payload: HermesError) {}
}

export class SessionCheckFail implements Action {
  readonly type = AuthActionTypes.SESSION_CHECK_FAIL;

  constructor(public payload: HermesError) {}
}

export class SessionExpiredFail implements Action {
  readonly type = AuthActionTypes.SESSION_EXPIRED_FAIL;

  constructor(public payload: HermesError) {}
}

export class LoadTokenRedirectRequiredFail implements Action {
  readonly type = AuthActionTypes.LOAD_TOKEN_REDIRECT_REQUIRED_FAIL;
}

export class LoadTokenOtherFail implements Action {
  readonly type = AuthActionTypes.LOAD_TOKEN_OTHER_FAIL;

  constructor(public payload: HermesError) {}
}

export class SessionCheckOtherFail implements Action {
  readonly type = AuthActionTypes.SESSION_CHECK_OTHER_FAIL;

  constructor(public payload: HermesError) {}
}

export class InvalidScopeFail implements Action {
  readonly type = AuthActionTypes.INVALID_SCOPE_FAIL;

  constructor(public payload: HttpError) {}
}

export class InternalFail implements Action {
  readonly type = AuthActionTypes.INTERNAL_FAIL;

  constructor(public payload: HttpError) {}
}

export class MaximumRetriesFail implements Action {
  readonly type = AuthActionTypes.MAXIMUM_RETRIES_FAIL;

  constructor(public payload: HttpError) {}
}

export class NoPermissionFail implements Action {
  readonly type = AuthActionTypes.NO_PERMISSION_FAIL;

  constructor(public payload: HttpError) {}
}

export class TokenExpiredFail implements Action {
  readonly type = AuthActionTypes.TOKEN_EXPIRED_FAIL;

  constructor(public payload: HttpError) {}
}

export class UpdateLocaleCookie implements Action {
  readonly type = AuthActionTypes.UPDATE_LOCALE_COOKIE;

  constructor(public payload: string) {}
}

export type AuthAction =
  | HandleLoginRedirect
  | InitLoginRedirect
  | InvalidScopeFail
  | InternalFail
  | LoadIframeTokenSuccess
  | LoadRTRTokenSuccess
  | LoadTokenFail
  | LoadTokenRedirectRequiredFail
  | LoadTokenOtherFail
  | LoadTokenUserPreferences
  | LoginUser
  | LogoutUser
  | LogoutUserComplete
  | MaximumRetriesFail
  | NoPermissionFail
  | RedirectToLogin
  | SessionCheckFail
  | SessionCheckOtherFail
  | SessionExpiredFail
  | SetLoginRef
  | StartIdleCheck
  | StartRTRTokenRefresh
  | StartSessionCheck
  | StartIframeTokenRefresh
  | TokenExpiredFail
  | UpdateLocaleCookie;
